import type { CameraErrorKind } from './barcodeCamera.ts';

export type ScanFeedbackTone = 'green' | 'amber' | 'red';

export type ScanFeedback = {
  tone: ScanFeedbackTone;
  title: string;
  detail: string;
};

export type ScanFeedbackSource =
  | { kind: 'scan_result'; result: string; barcode?: string }
  | { kind: 'duplicate_ignored'; barcode?: string }
  | { kind: 'camera_error'; cameraKind: CameraErrorKind; message?: string };

/**
 * Map scan arbitration results, debounce duplicates, and camera errors
 * to a short crew-facing feedback chip.
 */
export function mapScanFeedback(source: ScanFeedbackSource): ScanFeedback {
  if (source.kind === 'duplicate_ignored') {
    return {
      tone: 'amber',
      title: 'Already scanned',
      detail: source.barcode
        ? `${source.barcode} already counted – scan the next item`
        : 'Same barcode already counted – scan the next item',
    };
  }

  if (source.kind === 'camera_error') {
    return {
      tone: 'red',
      title: cameraErrorTitle(source.cameraKind),
      detail: source.message?.trim() || 'Camera unavailable. Use manual entry.',
    };
  }

  const barcode = source.barcode?.trim();
  switch (source.result) {
    case 'counted':
      return {
        tone: 'green',
        title: 'Counted',
        detail: barcode ? `${barcode} counted` : 'Barcode counted on manifest',
      };
    case 'over':
      return {
        tone: 'amber',
        title: 'Over count',
        detail: barcode
          ? `${barcode} exceeds expected quantity`
          : 'Scan exceeds expected quantity',
      };
    case 'not_on_manifest':
      return {
        tone: 'red',
        title: 'Not on list',
        detail: barcode ? `${barcode} is not on this stop` : 'Barcode is not on this stop',
      };
    case 'wrong_stop':
      return {
        tone: 'red',
        title: 'Wrong stop',
        detail: barcode
          ? `${barcode} belongs to another stop on this trip`
          : 'Barcode belongs to another stop on this trip',
      };
    case 'pending_confirmation':
      return {
        tone: 'amber',
        title: 'Pending confirmation',
        detail: barcode
          ? `${barcode} queued for confirmation`
          : 'Scan queued for confirmation',
      };
    case 'corrected':
      return {
        tone: 'green',
        title: 'Corrected',
        detail: barcode ? `${barcode} marked corrected` : 'Over-scan marked corrected',
      };
    default:
      return {
        tone: 'amber',
        title: 'Scan recorded',
        detail: barcode ? `${barcode} recorded` : 'Scan recorded',
      };
  }
}

function cameraErrorTitle(kind: CameraErrorKind): string {
  switch (kind) {
    case 'permission_denied':
      return 'Camera permission denied';
    case 'no_camera':
      return 'No camera found';
    case 'camera_in_use':
      return 'Camera in use';
    case 'unsupported':
      return 'Camera unsupported';
    case 'overconstrained':
      return 'Camera unavailable';
    default:
      return 'Camera error';
  }
}

let sharedAudioCtx: AudioContext | null = null;

/** One AudioContext for the whole session: creating/closing one per beep cut the
 * sound off on many Android phones and hits the browser's context limit. */
function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const AudioCtx =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioCtx) return null;
  if (!sharedAudioCtx || sharedAudioCtx.state === 'closed') {
    sharedAudioCtx = new AudioCtx();
  }
  if (sharedAudioCtx.state === 'suspended') {
    void sharedAudioCtx.resume().catch(() => undefined);
  }
  return sharedAudioCtx;
}

/** Audible beep via Web Audio (loud enough for a noisy warehouse); no-ops safely otherwise. */
export function playScanFeedbackTone(tone: ScanFeedbackTone): void {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = tone === 'green' ? 'sine' : 'square';
    osc.frequency.value = tone === 'green' ? 880 : tone === 'amber' ? 520 : 220;
    const duration = tone === 'green' ? 0.12 : 0.25;
    const now = ctx.currentTime;
    gain.gain.setValueAtTime(0.3, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.onended = () => {
      osc.disconnect();
      gain.disconnect();
    };
    osc.start(now);
    osc.stop(now + duration);
  } catch {
    /* unsupported / autoplay blocked */
  }
}

/** Short haptic pulse when Vibration API is available; no-ops otherwise. */
export function vibrateScanFeedback(tone: ScanFeedbackTone): void {
  try {
    if (typeof navigator === 'undefined' || typeof navigator.vibrate !== 'function') return;
    if (tone === 'green') navigator.vibrate(30);
    else if (tone === 'amber') navigator.vibrate([40, 40, 40]);
    else navigator.vibrate([80, 40, 80]);
  } catch {
    /* unsupported */
  }
}
